/**
 * ============================================================================
 * TABLE SELECTORS — derive table/tile views from getMetrics + the catalog.
 * ============================================================================
 *
 * The QuickSight WBR dashboard renders every visual (KPI tiles, network
 * summary tables, OTD tables) from ONE dataset. This module is the custom-UI
 * equivalent: all views derive from the same GetMetricsOutput, joined to the
 * getMetricDefinitions catalog by metricKey.
 *
 * - selectWeekColumns:     periods → ordered week columns with period group
 * - selectMetricTableRows: metric × week pivot (rows for MetricTableSection)
 * - selectNetworkTiles:    per-network rollup for the KPI tiles page
 *
 * Row identity and ordering come from the catalog (metricKey, sortOrder);
 * aggregation comes from aggregationCore. Neither is re-implemented here.
 */

import {
  GetMetricsOutput,
  DcrSnapshotData,
  DcrRecord,
  MetricDefinition,
} from 'src/types/metricsApi.types';
import {
  MetricCatalog,
  aggregateMetric,
  buildMetricCatalog,
  normalizeMetricType,
  parseReportingPeriod,
  perDcrRate,
} from 'src/features/api/aggregationCore';
import { matchesDemandCategory, demandCategoryIdForValue } from 'src/config/demandCategories';
import { MetricGroupSelection, metricGroupMatches } from 'src/features/api/filterPredicate';

/** Optional row-level predicate applied to DCRs before aggregation. */
export type DcrPredicate = (dcr: DcrRecord) => boolean;

// ──── Output types ────

export type PeriodGroup = 'historical' | 'current' | 'forward';

/** Column-group display titles (QuickSight parity: "Forward", not "Forecast"). */
export const PERIOD_GROUP_LABELS: Record<PeriodGroup, string> = {
  historical: '1. Historical Period',
  current: '2. Current Period',
  forward: '3. Forward Period',
};

export interface WeekColumn {
  /** Stable column key, e.g. "2026Week32". */
  key: string;
  /** Display label, e.g. "2026 Week 32". */
  label: string;
  group: PeriodGroup;
}

export interface MetricTableRow {
  /** Display name from the catalog (section configs match on this). */
  metric: string;
  /** Stable identity from the catalog ('' for client-derived rows). */
  metricKey: string;
  owner: string;
  goal: number;
  /** "GTE" = higher is better (default), "LTE" = lower is better. */
  goalDirection: string;
  /** True → render as percentage with RAG colouring vs goal. */
  isRate: boolean;
  /** Client-derived row (e.g. %DCRs≥threshold) — no callout panel. */
  derived?: boolean;
  /** WBR callout text ('' = none) — shown in the snapshot Callout column. */
  callout: string;
  /** Week key → aggregated value (null = no data for that week). */
  [weekKey: string]: number | string | boolean | null | undefined;
}

export interface NetworkTileMetric {
  name: string;
  isOnTrack: boolean;
  value?: number;
  target?: number;
  unit?: string;
}

export interface NetworkTile {
  id: string;
  name: string;
  metrics: NetworkTileMetric[];
  /** Aggregated primary rate (first percentage metric) for threshold colour. */
  primaryRatePct: number;
}

// ──── Shared helpers ────

function dcrsForNetwork(
  period: DcrSnapshotData,
  networkSlug?: string,
  predicate?: DcrPredicate,
): DcrRecord[] {
  let dcrs = period.dcrData;
  if (networkSlug && networkSlug !== 'all') {
    // Membership from the network registry (src/config/demandCategories.ts) —
    // the single source of truth; supports rollups (category lists).
    dcrs = dcrs.filter((dcr) => matchesDemandCategory(networkSlug, dcr.demandCategory));
  }
  return predicate ? dcrs.filter(predicate) : dcrs;
}

/**
 * Catalog metrics that appear in the data, in catalog (sortOrder) order,
 * restricted to the selected Metric Groups.
 */
function presentDefinitions(
  response: GetMetricsOutput,
  definitions: MetricDefinition[],
  network?: string,
  predicate?: DcrPredicate,
  metricGroups?: MetricGroupSelection,
): MetricDefinition[] {
  const present = new Set<string>();
  for (const period of response.data) {
    for (const dcr of dcrsForNetwork(period, network, predicate)) {
      for (const entry of dcr.metrics) present.add(entry.metricKey);
    }
  }
  // Metric Group filters VOLUME metrics only. Percentage (OTD) metrics belong
  // to the separate On Time Delivery % visual, which has fixed rows and ignores
  // the control — same rule as the tiles and the home-page cards.
  return definitions
    .filter((definition) => present.has(definition.metricKey))
    .filter(
      (definition) =>
        !metricGroups ||
        normalizeMetricType(definition.type) === 'percentage' ||
        metricGroupMatches(definition.metricGroup, metricGroups),
    )
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

// ──── Derived OTD threshold metrics (spec: derived-otd-threshold-metrics) ────
// QuickSight computes "% DCRs with OTD >= Threshold" as calculated fields over
// the per-DCR OTD rates vs the OTDThreshold parameter (default 95 — CTS
// quicksightStack.ts). The per-DCR rates arrive as their own catalog metrics
// (per_dcr_otd_*) which are value carriers: never rows, never averaged.

/** QuickSight OTDThreshold parameter default (staticValues: [95.0]). */
export const OTD_THRESHOLD_DEFAULT = 95;

interface DerivedOtdFamily {
  /** Per-DCR rate carrier metricKey (0-100 capped, one entry per DCR). */
  carrierKey: string;
  /** The network OTD row this derived row follows in the table. */
  networkRowKey: string;
  /** Derived row label — exactly as the QuickSight visual labels it. */
  derivedRow: string;
}

export const DERIVED_OTD_FAMILIES: DerivedOtdFamily[] = [
  {
    carrierKey: 'per_dcr_otd_against_requested_demand_nbd',
    networkRowKey: 'otd_against_requested_demand_nbd',
    derivedRow: '% DCRs with On Time Delivery >= Threshold (vs Requested Demand & NBD)',
  },
  {
    carrierKey: 'per_dcr_otd_against_committed_volume',
    networkRowKey: 'otd_against_committed_volume',
    derivedRow: '% DCRs with On Time Delivery >= Threshold (vs Committed Volume)',
  },
  {
    carrierKey: 'per_dcr_otd_against_ingested_committed_volume',
    networkRowKey: 'otd_against_ingested_committed_volume',
    derivedRow: '% DCRs with On Time Delivery >= Threshold (vs Ingested Committed Volume)',
  },
];

/** Per-DCR OTD rate carriers: never rendered anywhere, never aggregated. */
export const OTD_CARRIER_KEYS = new Set(DERIVED_OTD_FAMILIES.map((family) => family.carrierKey));

// ──── Selectors ────

export function selectWeekColumns(response: GetMetricsOutput | undefined): WeekColumn[] {
  if (!response) return [];
  return response.data.map((period) => {
    const { key, label } = parseReportingPeriod(period.reportingPeriod);
    const group: PeriodGroup =
      period.timeline === 'CURRENT' ? 'current' : period.timeline === 'FUTURE' ? 'forward' : 'historical';
    return { key, label, group };
  });
}

/**
 * Pivot metric × week. `types` filters which metric kinds appear (the
 * Executive Summary "volumes" section vs the "OTD %" section); omit for all.
 */
export function selectMetricTableRows(
  response: GetMetricsOutput | undefined,
  definitions: MetricDefinition[] | undefined,
  options: {
    types?: Array<'percentage' | 'volume' | 'count'>;
    network?: string;
    predicate?: DcrPredicate;
    /** OTDThreshold parameter for derived %DCRs rows (default 95). */
    otdThreshold?: number;
    /** Metric Group chips — filters metric ROWS (not DCR rows). */
    metricGroups?: MetricGroupSelection;
  } = {},
): MetricTableRow[] {
  if (!response || response.data.length === 0 || !definitions?.length) return [];
  const { types, network, predicate, metricGroups } = options;
  const otdThreshold = options.otdThreshold ?? OTD_THRESHOLD_DEFAULT;
  const catalog: MetricCatalog = buildMetricCatalog(definitions);
  const weekColumns = selectWeekColumns(response);

  const result: MetricTableRow[] = [];
  for (const definition of presentDefinitions(response, definitions, network, predicate, metricGroups)) {
    const type = normalizeMetricType(definition.type);
    if (types && !types.includes(type)) continue;
    // Per-DCR OTD rates are value carriers for the derived %DCRs rows —
    // never table rows, never averaged (spec derived-otd R3).
    if (OTD_CARRIER_KEYS.has(definition.metricKey)) continue;

    const row: MetricTableRow = {
      metric: definition.name,
      metricKey: definition.metricKey,
      owner: definition.owner ?? '',
      goal: parseFloat(definition.goal ?? '0') || 0,
      goalDirection: (definition.goalDirection ?? 'GTE').toUpperCase(),
      isRate: type === 'percentage',
      // Callouts come from the ListMetricComments API, not getMetrics.
      callout: '',
    };
    response.data.forEach((period, weekIndex) => {
      const dcrs = dcrsForNetwork(period, network, predicate);
      row[weekColumns[weekIndex].key] = aggregateMetric(dcrs, definition.metricKey, catalog).value;
    });
    result.push(row);
  }

  // Derived "% DCRs with OTD >= Threshold" rows (QuickSight calculated-field
  // parity): 100 × count(perDcrRate ≥ threshold) / count(carrying DCRs) per
  // week over the SAME filtered slice; weeks with no carriers stay null.
  // Rows insert directly after their family's network OTD row (QS order).
  // Derived rows follow their family's network row, so they only appear when
  // that row's group passes the Metric Group selection.
  const otdGroupSelected =
    !metricGroups ||
    definitions.some(
      (definition) =>
        DERIVED_OTD_FAMILIES.some((family) => family.networkRowKey === definition.metricKey) &&
        metricGroupMatches(definition.metricGroup, metricGroups),
    );
  if ((!types || types.includes('percentage')) && otdGroupSelected) {
    for (const family of DERIVED_OTD_FAMILIES) {
      const derivedRow: MetricTableRow = {
        metric: family.derivedRow,
        metricKey: '',
        owner: '',
        goal: 0,
        goalDirection: 'GTE',
        isRate: true,
        derived: true,
        callout: '',
      };
      let seen = false;
      response.data.forEach((period, weekIndex) => {
        const dcrs = dcrsForNetwork(period, network, predicate);
        let carrying = 0;
        let hits = 0;
        for (const dcr of dcrs) {
          const entry = dcr.metrics.find((x) => x.metricKey === family.carrierKey);
          if (!entry) continue;
          const rate = perDcrRate(entry);
          if (rate === null) continue;
          carrying += 1;
          if (rate >= otdThreshold) hits += 1;
        }
        derivedRow[weekColumns[weekIndex].key] = carrying > 0 ? (100 * hits) / carrying : null;
        if (carrying > 0) seen = true;
      });
      if (!seen) continue; // no carriers anywhere → no row (never a row of zeros)
      const familyIndex = result.findIndex((row) => row.metricKey === family.networkRowKey);
      result.splice(familyIndex >= 0 ? familyIndex + 1 : result.length, 0, derivedRow);
    }
  }

  return result;
}

// ──── Derived DSL Coverage rows (spec: dsl-coverage-snapshot) ────
// QuickSight's snapshot sheet shows a "{Category} - DSL Coverage" pivot per
// demand category, computed as calculated fields over the frozen
// collection_owner column (CTS quicksightStack.ts: DSL_Coverage_Pct,
// Avg_DCRs_Per_DSL). Neither is a catalog metric — like the %DCRs≥threshold
// rows, they derive client-side from the DCR records. Snapshot-only:
// collection_owner is captured at snapshot time, so LIVE data may not carry it.

/**
 * Catalog metricKeys for the definition-only DSL entries. When the catalog
 * carries them, their name/owner/goal/goalDirection win; otherwise the
 * constants below apply (QuickSight visual-subtitle parity). No values are
 * ever published for these keys, so they can never appear as catalog rows
 * in the regular sections (presentDefinitions requires data presence).
 */
export const DSL_COVERAGE_PCT_KEY = 'dsl_coverage_pct';
export const AVG_DCRS_PER_DSL_KEY = 'avg_dcrs_per_dsl';

/** QuickSight goal: DSL Coverage % ≥ 95 (GTE). */
export const DSL_COVERAGE_GOAL = 95;
/** QuickSight goal: Avg DCRs per DSL ≤ 7 (LTE). */
export const AVG_DCRS_PER_DSL_GOAL = 7;
/** Section owner, as the QuickSight visual subtitle shows it. */
export const DSL_COVERAGE_OWNER = 'robgill';

/** An assigned owner is non-null and non-blank (QS distinct_count skips NULL). */
function dslOwnerOf(dcr: DcrRecord): string | null {
  const owner = dcr.collectionOwner?.trim();
  return owner ? owner : null;
}

/** Presentation for one derived DSL row: catalog entry first, fallback second. */
function dslRowPresentation(
  definitions: MetricDefinition[] | undefined,
  metricKey: string,
  fallback: { metric: string; goal: number; goalDirection: string },
): Pick<MetricTableRow, 'metric' | 'metricKey' | 'owner' | 'goal' | 'goalDirection'> {
  const definition = definitions?.find((d) => d.metricKey === metricKey);
  if (!definition) {
    return { ...fallback, metricKey: '', owner: DSL_COVERAGE_OWNER };
  }
  return {
    metric: definition.name,
    metricKey: definition.metricKey,
    owner: definition.owner ?? DSL_COVERAGE_OWNER,
    goal: parseFloat(definition.goal ?? '') || fallback.goal,
    goalDirection: (definition.goalDirection ?? fallback.goalDirection).toUpperCase(),
  };
}

/**
 * Derive the two DSL Coverage rows for one network scope, per week:
 *
 *   DSL Coverage %   = 100 × distinct DCRs with owner / distinct DCRs
 *   Avg DCRs per DSL = distinct DCRs with owner / distinct owners
 *
 * QuickSight parity (calculated fields over the snapshot dataset): weeks with
 * no DCRs (or, for the average, no owners) are null, never 0. Computed over
 * the SAME filtered slice as every other cell, so filter chips propagate.
 * Returns [] when no week has any DCR — the section shows its empty state.
 *
 * Presentation is catalog-first: the dsl_coverage_pct / avg_dcrs_per_dsl
 * entries supply name/owner/goal when present in the passed catalog (values
 * stay client-derived — the backend never publishes them).
 */
export function selectDslCoverageRows(
  response: GetMetricsOutput | undefined,
  options: {
    network?: string;
    predicate?: DcrPredicate;
    definitions?: MetricDefinition[];
  } = {},
): MetricTableRow[] {
  if (!response || response.data.length === 0) return [];
  const weekColumns = selectWeekColumns(response);

  const coverageRow: MetricTableRow = {
    ...dslRowPresentation(options.definitions, DSL_COVERAGE_PCT_KEY, {
      metric: 'DSL Coverage %',
      goal: DSL_COVERAGE_GOAL,
      goalDirection: 'GTE',
    }),
    isRate: true,
    derived: true,
    callout: '',
  };
  const avgRow: MetricTableRow = {
    ...dslRowPresentation(options.definitions, AVG_DCRS_PER_DSL_KEY, {
      metric: 'Avg DCRs per DSL',
      goal: AVG_DCRS_PER_DSL_GOAL,
      goalDirection: 'LTE',
    }),
    isRate: false,
    derived: true,
    callout: '',
  };

  let seen = false;
  response.data.forEach((periodData, weekIndex) => {
    const weekKey = weekColumns[weekIndex].key;
    const dcrs = dcrsForNetwork(periodData, options.network, options.predicate);
    const total = new Set(dcrs.map((dcr) => dcr.dcrId)).size;
    const withOwner = new Set(
      dcrs.filter((dcr) => dslOwnerOf(dcr) !== null).map((dcr) => dcr.dcrId),
    ).size;
    const owners = new Set(
      dcrs.map(dslOwnerOf).filter((owner): owner is string => owner !== null),
    ).size;
    coverageRow[weekKey] = total > 0 ? (100 * withOwner) / total : null;
    // 1 decimal, matching the QuickSight number format (non-rate cells render
    // the value as-is).
    avgRow[weekKey] = owners > 0 ? Math.round((withOwner / owners) * 10) / 10 : null;
    if (total > 0) seen = true;
  });

  return seen ? [coverageRow, avgRow] : [];
}

/**
 * Roll up the CURRENT week per network (DCR demandCategory) for the KPI
 * tiles page. Each tile lists that network's metrics with on/off-track
 * status vs their goals.
 */
export function selectNetworkTiles(
  response: GetMetricsOutput | undefined,
  definitions: MetricDefinition[] | undefined,
  predicate?: DcrPredicate,
  metricGroups?: MetricGroupSelection,
): NetworkTile[] {
  if (!response || response.data.length === 0 || !definitions?.length) return [];
  const catalog = buildMetricCatalog(definitions);
  const current =
    response.data.find((period) => period.timeline === 'CURRENT') ??
    response.data[response.data.length - 1];
  const currentDcrs = predicate ? current.dcrData.filter(predicate) : current.dcrData;

  const byNetwork = new Map<string, DcrRecord[]>();
  for (const dcr of currentDcrs) {
    const category = dcr.demandCategory ?? 'Others';
    if (!byNetwork.has(category)) byNetwork.set(category, []);
    byNetwork.get(category)!.push(dcr);
  }

  // Metric Group scopes volume metrics only — percentage (OTD) metrics always
  // surface, matching the dashboard's separate On Time Delivery % visual.
  const orderedDefinitions = definitions
    .filter(
      (definition) =>
        !metricGroups ||
        normalizeMetricType(definition.type) === 'percentage' ||
        metricGroupMatches(definition.metricGroup, metricGroups),
    )
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const tiles: NetworkTile[] = [];
  byNetwork.forEach((dcrs, category) => {
    let primaryRatePct = 100;
    let primaryFound = false;
    const tileMetrics: NetworkTileMetric[] = [];

    for (const definition of orderedDefinitions) {
      // Per-DCR carriers never surface on tiles.
      if (OTD_CARRIER_KEYS.has(definition.metricKey)) continue;
      const { value, isRate } = aggregateMetric(dcrs, definition.metricKey, catalog);
      if (value === null) continue;
      const goal = parseFloat(definition.goal ?? '0') || 0;
      const higherIsBetter = (definition.goalDirection ?? 'GTE').toUpperCase() !== 'LTE';
      const isOnTrack = goal === 0 ? true : higherIsBetter ? value >= goal : value <= goal;
      tileMetrics.push({
        name: definition.name,
        isOnTrack,
        value: Math.round(value * 10) / 10,
        target: goal || undefined,
        unit: isRate ? '%' : undefined,
      });
      if (isRate && !primaryFound) {
        primaryRatePct = value;
        primaryFound = true;
      }
    }

    tiles.push({
      id: demandCategoryIdForValue(category) ?? category.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      name: category,
      metrics: tileMetrics,
      primaryRatePct,
    });
  });

  return tiles;
}

// ──── Trend (spec: executive-summary-trends) ────

export interface MetricTrend {
  /** Weekly aggregated values, historical → current order, null weeks skipped. */
  values: number[];
  /** Week labels aligned to `values` (skipped weeks stay aligned). */
  weekLabels: string[];
  /** Current-week value (null when the week has no data). */
  current: number | null;
  /** Last historical week's value with data (null when none). */
  previous: number | null;
}

/**
 * Derive the trend series for one pivoted row. Forward (forecast) weeks are
 * excluded — a trend is what happened, not what's planned. Pure function over
 * already-pivoted data, so filter changes recompute it for free (spec R1.5).
 */
export function selectMetricTrend(row: MetricTableRow, weeks: WeekColumn[]): MetricTrend {
  const values: number[] = [];
  const weekLabels: string[] = [];

  for (const week of weeks) {
    if (week.group === 'forward') continue;
    const value = row[week.key];
    if (typeof value === 'number') {
      values.push(value);
      weekLabels.push(week.label);
    }
  }

  const currentWeek = weeks.find((w) => w.group === 'current');
  const currentRaw = currentWeek ? row[currentWeek.key] : undefined;
  const current = typeof currentRaw === 'number' ? currentRaw : null;

  let previous: number | null = null;
  const historical = weeks.filter((w) => w.group === 'historical');
  for (let i = historical.length - 1; i >= 0; i--) {
    const value = row[historical[i].key];
    if (typeof value === 'number') {
      previous = value;
      break;
    }
  }

  return { values, weekLabels, current, previous };
}
